import { useEffect } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';

import DeclinedDecision from './DeclinedDecision';
import DeferredDecision from './DeferredDecision';
import OfferedDecision from './OfferedDecision';
import Open from './OpenDecision';
import {
  useGetApplicationQuery,
  useGetContext,
  useGetFormalOffers,
} from '../../api/lendingApi/lendingApiQueries';
import { ISOLATED_SPLIT_TESTS } from '../../constants';
import { getSplitTestFromContext } from '../../hooks/useSplitTestContext';
import { getAmplitudeDefaultEventProperties } from '../../utils/amplitudeEvents';
import ApplicationLoader from '../ApplicationLoader/ApplicationLoader';

type TDecisionStatus = 'declined' | 'deferred' | 'offered' | 'open';

const Decision = () => {
  const { application, applicationStatus, isApplicationRefetching } =
    useGetApplicationQuery();

  const { formalOffersData, formalOffersLoading } = useGetFormalOffers();

  // The cost-framing split test is exposed on the context endpoint, so we read
  // it from there rather than making a dedicated split-test request. An entry
  // is present (value true/false) once the user is assigned; absent otherwise.
  const { context, isContextLoading } = useGetContext();
  const costFramingSplitTest = getSplitTestFromContext(
    ISOLATED_SPLIT_TESTS.PARTNER_DECISION_PAGE_COST_FRAMING_2026,
    context,
  );
  const splitTestBranch = costFramingSplitTest
    ? costFramingSplitTest[
        ISOLATED_SPLIT_TESTS.PARTNER_DECISION_PAGE_COST_FRAMING_2026
      ]
      ? 'INCLUDE'
      : 'EXCLUDE'
    : null;

  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': 'Decision',
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  const decisionStatus = (): TDecisionStatus => {
    if (application?.status === 'declined') return 'declined';
    if (application?.status === 'deferred') return 'deferred';

    const isOfferedAndNotMicroApproval =
      application?.is_closed &&
      application?.status === 'offered' &&
      formalOffersData;

    if (isOfferedAndNotMicroApproval) return 'offered';

    return 'open';
  };

  if (
    !application ||
    applicationStatus === 'pending' ||
    formalOffersLoading ||
    isApplicationRefetching ||
    // Wait for the context (which carries the cost-framing split test) to
    // resolve so INCLUDE users don't briefly see the control layout first.
    isContextLoading
  ) {
    return <ApplicationLoader />;
  }

  if (decisionStatus() === 'declined') {
    return <DeclinedDecision />;
  }

  if (decisionStatus() === 'deferred') {
    return <DeferredDecision />;
  }

  if (decisionStatus() === 'offered' && formalOffersData) {
    return (
      <OfferedDecision
        formalOffersData={formalOffersData}
        splitTestBranch={splitTestBranch}
      />
    );
  }

  return <Open />;
};

export default Decision;
